import React from 'react';
import { Notifier } from '@airbrake/browser';

import { INotice, INoticeError } from 'utils/types';
import { GET_CONTEXT_NULL, INVALID_ADDRESS } from 'utils/errors';

export const airbrake = new Notifier({
	projectId: Number(process.env.REACT_APP_AIRBRAKE_PROJECT_ID),
	projectKey: String(process.env.REACT_APP_AIRBRAKE_PROJECT_KEY),
	environment: String(process.env.REACT_APP_AIRBRAKE_ENVIRONMENT),
});

airbrake.addFilter(function (notice: INotice) {
	const errors = notice.errors as INoticeError[];
	const error = errors[0];
	if (error.message === GET_CONTEXT_NULL || error.message === INVALID_ADDRESS) {
		return null;
	}
	return notice;
});

interface State {
	hasError: boolean;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		airbrake.notify({ error, params: { info: info.componentStack } });
		window.parent.postMessage(
			{ action: 'error', message: error.message, stack: error.stack, componentStack: info.componentStack },
			'*'
		);
	}

	componentDidMount() {
		window.onerror = (message, _source, _lineno, _colno, error) => {
			airbrake.notify({ error: error ?? String(message) });
			window.parent.postMessage({ action: 'error', message: String(message), stack: error?.stack }, '*');
		};
	}

	componentWillUnmount() {
		window.onerror = null;
	}

	render() {
		if (this.state.hasError) {
			const isPunchup = window.location.search.includes('isPunchup=true');

			if (isPunchup) {
				return (
					<div
						style={{
							width: '100%',
							boxSizing: 'border-box',
							padding: '16px 16px 4px',
						}}
					>
						<div
							style={{
								maxWidth: '768px',
								border: '1px solid #FECACA',
								borderRadius: '8px',
								overflow: 'hidden',
								backgroundColor: '#FFF5F5',
								padding: '20px 24px',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									style={{ flexShrink: 0, marginTop: '1px', color: '#DC2626' }}
								>
									<path
										d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
										stroke="#DC2626"
										strokeWidth="1.5"
										fill="none"
										strokeLinejoin="round"
									/>
									<line x1="12" y1="9" x2="12" y2="13" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" />
									<line x1="12" y1="17" x2="12.01" y2="17" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
								</svg>
								<div style={{ flex: 1 }}>
									<p
										style={{
											margin: '0 0 4px',
											fontSize: '14px',
											fontWeight: 600,
											fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
											color: '#991B1B',
											lineHeight: '1.4',
										}}
									>
										Unable to load show dashboard
									</p>
									<p
										style={{
											margin: 0,
											fontSize: '14px',
											fontWeight: 400,
											fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
											color: '#B91C1C',
											lineHeight: '1.5',
										}}
									>
										We&apos;re having trouble loading this page. Our team has been notified and is looking into it.
										Please{' '}
										<button
											onClick={() => window.location.reload()}
											style={{
												background: 'none',
												border: 'none',
												padding: 0,
												color: '#DC2626',
												fontWeight: 600,
												fontSize: '14px',
												fontFamily: 'inherit',
												cursor: 'pointer',
												textDecoration: 'underline',
											}}
										>
											refresh the page
										</button>{' '}
										to try again.
									</p>
								</div>
							</div>
						</div>
					</div>
				);
			}

			return <div>Something went wrong. Please refresh the page.</div>;
		}
		return this.props.children;
	}
}

export default ErrorBoundary;
